import React from 'react';
import { GoalPhase } from './PhaseIndicator';
import { GoalIcon, ProfileIcon, MilestoneIcon, ChatIcon, CheckIcon, GrowthIcon, QuickIcon } from '../../../icons';
import { Sparkles } from 'lucide-react';

// =============================================================================
// ⚓ ANCHORED: 2026-01-27
// Fase Voltooiing Kaarten - Tonen wanneer een fase is afgerond
// Zorgt voor intuïtieve doorstroom naar de volgende fase
// =============================================================================

interface PhaseCompletionCardProps {
  completedPhase: GoalPhase;
  onContinue: () => void;
  onDismiss?: () => void;
}

// Configuratie per fase
const PHASE_CONFIG: Record<GoalPhase, {
  title: string;
  completedMessage: string;
  nextPhaseLabel: string;
  nextPhaseDescription: string;
  icon: React.ReactNode;
  nextIcon: React.ReactNode;
  gradient: string;
}> = {
  intake: {
    title: 'Hoofddoel Vastgelegd!',
    completedMessage: 'Je hebt je doel gedefinieerd en je commitment vastgelegd.',
    nextPhaseLabel: 'Profiel',
    nextPhaseDescription: 'Bepaal je type ondernemer en kennisniveau',
    icon: <GoalIcon size={48} className="text-white" />,
    nextIcon: <ProfileIcon size={24} className="text-gray-700" />,
    gradient: 'from-blue-500 to-blue-600',
  },
  profile: {
    title: 'Profiel Compleet!',
    completedMessage: 'Je ondernemersprofiel en kennisniveau zijn vastgelegd. Je eerste mijlpaal "Persoonlijk Profiel & Zelfontwikkeling" is toegevoegd!',
    nextPhaseLabel: 'Skills',
    nextPhaseDescription: 'Bepaal je capaciteiten en hoe je gaps gaat oplossen',
    icon: <ProfileIcon size={48} className="text-white" />,
    nextIcon: <QuickIcon size={24} className="text-gray-700" />,
    gradient: 'from-purple-500 to-purple-600',
  },
  capability: {
    title: 'Mijlpalen Opgeslagen!',
    completedMessage: 'Je mijlpalen en acties zijn vastgelegd op basis van je assessment.',
    nextPhaseLabel: 'Sparring',
    nextPhaseDescription: 'Verdiep je strategie en tijdlijn in een gesprek (optioneel)',
    icon: <MilestoneIcon size={48} className="text-white" />,
    nextIcon: <ChatIcon size={24} className="text-gray-700" />,
    gradient: 'from-green-500 to-emerald-600',
  },
  sparring: {
    title: 'Sparring Voltooid!',
    completedMessage: 'Je strategie en aanpak zijn verkend.',
    nextPhaseLabel: 'Mijlpalen',
    nextPhaseDescription: 'Genereer en bekijk je mijlpalen',
    icon: <ChatIcon size={48} className="text-white" />,
    nextIcon: <MilestoneIcon size={24} className="text-gray-700" />,
    gradient: 'from-green-500 to-green-600',
  },
  milestones: {
    title: 'Mijlpalen Vastgelegd!',
    completedMessage: 'Je tussenresultaten staan op de tijdlijn.',
    nextPhaseLabel: 'Acties',
    nextPhaseDescription: 'Begin met het afvinken van je taken',
    icon: <MilestoneIcon size={48} className="text-white" />,
    nextIcon: <CheckIcon size={24} className="text-gray-700" />,
    gradient: 'from-teal-500 to-teal-600',
  },
  actions: {
    title: 'Acties Klaar!',
    completedMessage: 'Je hebt alle acties afgerond.',
    nextPhaseLabel: 'Uitvoering',
    nextPhaseDescription: 'Ga aan de slag met je werk',
    icon: <CheckIcon size={48} className="text-white" />,
    nextIcon: <GrowthIcon size={24} className="text-gray-700" />,
    gradient: 'from-indigo-500 to-indigo-600',
  },
  execution: {
    title: 'Doel Bereikt!',
    completedMessage: 'Gefeliciteerd! Je hebt je doel bereikt.',
    nextPhaseLabel: 'Nieuw Doel',
    nextPhaseDescription: 'Start een nieuw avontuur',
    icon: <GrowthIcon size={48} className="text-white" />,
    nextIcon: <GoalIcon size={24} className="text-gray-700" />,
    gradient: 'from-yellow-500 to-yellow-600',
  },
};

/**
 * PhaseCompletionCard
 * 
 * Wordt getoond wanneer een fase is afgerond.
 * Geeft duidelijke call-to-action naar de volgende fase.
 */
const PhaseCompletionCard: React.FC<PhaseCompletionCardProps> = ({
  completedPhase,
  onContinue,
  onDismiss,
}) => {
  const config = PHASE_CONFIG[completedPhase];
  
  // Don't show card for execution phase completion (it's the last one)
  if (completedPhase === 'execution') {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <style>{`
        @keyframes celebrateIn {
          0% {
            transform: scale(0.8) translateY(20px);
            opacity: 0;
          }
          50% {
            transform: scale(1.02);
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(-20px) rotate(180deg); opacity: 0; }
        }
      `}</style>
      
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        style={{ animation: 'celebrateIn 0.4s ease-out' }}
      >
        {/* Header met gradient */}
        <div className={`bg-gradient-to-r ${config.gradient} px-6 py-8 text-white text-center relative overflow-hidden`}>
          {/* Confetti effect */}
          <div className="absolute inset-0 pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="absolute"
                style={{
                  left: `${15 + i * 18}%`,
                  top: '10%',
                  animation: `confetti 1.5s ease-out ${i * 0.1}s infinite`,
                }}
              >
                <Sparkles size={20} className="text-white/80" />
              </span>
            ))}
          </div>
          
          <div className="mb-3 flex justify-center" style={{ animation: 'celebrateIn 0.5s ease-out 0.1s both' }}>
            {config.icon}
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {config.title}
          </h2>
          <p className="text-white/90 text-sm">
            {config.completedMessage}
          </p>
        </div>

        {/* Body met volgende fase */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <span>{config.nextIcon}</span>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Volgende: {config.nextPhaseLabel}
                </h3>
                <p className="text-sm text-gray-600">
                  {config.nextPhaseDescription}
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={onContinue}
              className={`w-full py-4 px-6 bg-gradient-to-r ${config.gradient} text-white rounded-xl text-lg font-bold hover:opacity-90 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-3`}
            >
              <span>Ga verder met {config.nextPhaseLabel}</span>
              <span className="text-xl">→</span>
            </button>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="w-full py-2 px-4 text-gray-500 hover:text-gray-700 text-sm transition-colors"
              >
                Later doen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhaseCompletionCard;
